import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { JsonEditor } from '@library'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { DEFAULT_MOBILE_MAP } from '@mobileConstants'
import { ActionsConfigTab } from '@mobileComponents/EventFlow'
import { TabPanel } from '@mobileComponents/TabPanel'
import { useMobileMaps } from '@mobileHooks'
import {
  MobileMap,
  MobileMapPointGroup,
  MobileMapRouteTarget,
} from '@mobileTypes'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Tab,
  Tabs,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { VariablesConfigTab } from '@mobileComponents/Variables'
import { generateDataGridActionsColumn } from '@helpers'

import { ROUTES } from '@constants'

export const MobileMapCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const { createMap, updateMap, getMapById } = useMobileMaps()

  const methods = useForm<MobileMap>({
    defaultValues: isEdit ? getMapById(id!) || DEFAULT_MOBILE_MAP : DEFAULT_MOBILE_MAP,
  })
  const { control, handleSubmit, reset, watch, setValue, formState } = methods
  const { isDirty } = formState
  const formData = watch()

  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')

  useEffect(() => {
    if (isEdit && id) {
      const map = getMapById(id)
      if (map) {
        reset(map)
      }
    }
  }, [id, isEdit, getMapById, reset])

  useEffect(() => {
    if (tabValue === 4) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [tabValue, formData])

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      Object.keys(parsed).forEach(key => {
        setValue(key as keyof MobileMap, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch {
      setJsonValue(newValue)
    }
  }

  const [pointGroupDialogOpen, setPointGroupDialogOpen] = useState(false)
  const [pointGroupEditIndex, setPointGroupEditIndex] = useState<number | null>(null)
  const [pointGroupForm, setPointGroupForm] = useState<Partial<MobileMapPointGroup>>({})

  const onSubmit = (data: MobileMap) => {
    if (isEdit && id) {
      updateMap(id, data)
    } else {
      const newMap = createMap(data)
      navigate(`${ROUTES.CONFIG_MOBILE_MAPS_EDIT}/${newMap.id}`, { replace: true })
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_MAPS)
  }

  return (
    <MobileConfiguratorLayout
      title={isEdit ? 'Edit Mobile Map' : 'Create Mobile Map'}
      isEdit={isEdit}
      onSubmit={handleSubmit(onSubmit)}
      onCancel={handleCancel}
      isDirty={isDirty}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Basic Info" />
            <Tab label="Config" />
            <Tab label="Variables" />
            <Tab label="Actions" />
            <Tab label="JSON Editor" />
          </Tabs>
        </Paper>

        <FormProvider {...methods}>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField fullWidth label="Name" required {...field} />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="internalId"
                control={control}
                render={({ field }) => (
                  <TextField fullWidth label="Internal ID" {...field} placeholder="Unique id for config" />
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="mapCenter.0"
                control={control}
                render={() => (
                  <TextField
                    fullWidth
                    type="number"
                    label="Center longitude"
                    value={Array.isArray(formData.mapCenter) ? formData.mapCenter[0] : 37.6}
                    onChange={e =>
                      setValue('mapCenter', [
                        parseFloat(e.target.value) || 0,
                        Array.isArray(formData.mapCenter) ? formData.mapCenter[1] : 55.75,
                      ] as [number, number], { shouldDirty: true })
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="mapCenter.1"
                control={control}
                render={() => (
                  <TextField
                    fullWidth
                    type="number"
                    label="Center latitude"
                    value={Array.isArray(formData.mapCenter) ? formData.mapCenter[1] : 55.75}
                    onChange={e =>
                      setValue('mapCenter', [
                        Array.isArray(formData.mapCenter) ? formData.mapCenter[0] : 37.6,
                        parseFloat(e.target.value) || 0,
                      ] as [number, number], { shouldDirty: true })
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="mapZoom"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    type="number"
                    label="Zoom"
                    inputProps={{ min: 1, max: 20 }}
                    value={field.value ?? 12}
                    onChange={e => field.onChange(parseFloat(e.target.value) || 12)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="showCurrentLocation"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    }
                    label="Show current location"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Point groups</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setPointGroupForm({ title: '', variableName: '', color: '#3388ff' })
                      setPointGroupEditIndex(null)
                      setPointGroupDialogOpen(true)
                    }}
                  >
                    Add group
                  </Button>
                </Box>
                {Array.isArray(formData.mapPointGroups) && formData.mapPointGroups.length > 0 ? (
                  <DataGrid
                    rows={formData.mapPointGroups}
                    columns={[
                      { field: 'internalId', headerName: 'Internal ID', flex: 1, minWidth: 100 },
                      { field: 'title', headerName: 'Title', flex: 1, minWidth: 100 },
                      { field: 'variableName', headerName: 'Variable', flex: 1, minWidth: 100 },
                      { field: 'color', headerName: 'Color', flex: 1, minWidth: 80 },
                      generateDataGridActionsColumn({
                        onEdit: params => {
                          const idx = formData.mapPointGroups?.findIndex(
                            (g: MobileMapPointGroup) => g.internalId === params.row.internalId,
                          )
                          if (idx !== undefined && idx >= 0) {
                            setPointGroupForm(formData.mapPointGroups![idx])
                            setPointGroupEditIndex(idx)
                            setPointGroupDialogOpen(true)
                          }
                        },
                        onDelete: params => {
                          const arr = (formData.mapPointGroups || []).filter(
                            (g: MobileMapPointGroup) => g.internalId !== params.row.internalId,
                          )
                          setValue('mapPointGroups', arr, { shouldDirty: true })
                        },
                      }),
                    ]}
                    hideFooter
                    getRowId={(row: MobileMapPointGroup) => row.internalId}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No point groups. Add a group; each group uses a variable (array of objects with latitude,
                    longitude).
                  </Typography>
                )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="mapRouteTargetPoint"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Route target variable</InputLabel>
                    <Select
                      value={field.value?.variableName ?? ''}
                      label="Route target variable"
                      onChange={e =>
                        field.onChange(
                          e.target.value
                            ? ({ variableName: e.target.value } as MobileMapRouteTarget)
                            : undefined,
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {(formData.variables || []).map((v: { internalId: string }) => (
                        <MenuItem key={v.internalId} value={v.internalId}>
                          {v.internalId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <VariablesConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <ActionsConfigTab isMapContext />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <JsonEditor
            value={jsonValue}
            onChange={handleJsonChange}
            label="Map Configuration (JSON)"
            minHeight={400}
          />
        </TabPanel>
        </FormProvider>
      </Box>

      {/* Point group dialog */}
      <Dialog open={pointGroupDialogOpen} onClose={() => setPointGroupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{pointGroupEditIndex !== null ? 'Edit point group' : 'Add point group'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Internal ID"
              value={pointGroupForm.internalId ?? ''}
              onChange={e => setPointGroupForm(s => ({ ...s, internalId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Title"
              value={pointGroupForm.title ?? ''}
              onChange={e => setPointGroupForm(s => ({ ...s, title: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Variable</InputLabel>
              <Select
                value={pointGroupForm.variableName ?? ''}
                label="Variable"
                onChange={e => setPointGroupForm(s => ({ ...s, variableName: e.target.value }))}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {(formData.variables || []).map((v: { internalId: string }) => (
                  <MenuItem key={v.internalId} value={v.internalId}>{v.internalId}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Color"
              value={pointGroupForm.color ?? '#3388ff'}
              onChange={e => setPointGroupForm(s => ({ ...s, color: e.target.value }))}
              fullWidth
              placeholder="#3388ff"
            />
            <TextField
              label="Icon (optional)"
              value={pointGroupForm.icon ?? ''}
              onChange={e => setPointGroupForm(s => ({ ...s, icon: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Title field (optional)"
              value={pointGroupForm.titleField ?? ''}
              onChange={e => setPointGroupForm(s => ({ ...s, titleField: e.target.value }))}
              fullWidth
              placeholder="Field name for marker label"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPointGroupDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!pointGroupForm.internalId || !pointGroupForm.variableName || !pointGroupForm.title) return
              const arr = [...(formData.mapPointGroups || [])]
              const group: MobileMapPointGroup = {
                internalId: pointGroupForm.internalId,
                title: pointGroupForm.title,
                variableName: pointGroupForm.variableName,
                color: pointGroupForm.color ?? '#3388ff',
                icon: pointGroupForm.icon,
                titleField: pointGroupForm.titleField,
              }
              if (pointGroupEditIndex !== null) {
                arr[pointGroupEditIndex] = group
              } else {
                arr.push(group)
              }
              setValue('mapPointGroups', arr, { shouldDirty: true })
              setPointGroupDialogOpen(false)
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </MobileConfiguratorLayout>
  )
}
